import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CalendarIcon, ArrowRightIcon, ChevronRightIcon, EyeOpenIcon, MessageCircleIcon } from '../components/icons/Icons';
import CommentSection from '../components/CommentSection';
import api from '../services/api';
import './BlogPost.css';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/blog/slug/${slug}`);
      setPost(response.data);
    } catch (err) {
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... | Fuwali Investments Blog</title>
        </Helmet>
        <div className="blog-post-loading">
          <div className="loading-spinner-large"></div>
          <p>Loading post...</p>
        </div>
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <Helmet>
          <title>Post Not Found | Fuwali Investments Blog</title>
        </Helmet>
        <div className="blog-post-error">
          <h2>Post Not Found</h2>
          <p>The post you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="btn btn-primary">
            Back to Blog <ArrowRightIcon size={18} />
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title || 'Blog Post'} | Fuwali Investments Blog</title>
        <meta name="description" content={post.excerpt || post.title || 'Read this blog post from Fuwali Investments'} />
        <link rel="canonical" href={`https://fuwalizambia.com/blog/${post.slug}`} />
        <meta property="og:title" content={`${post.title} | Fuwali Investments Blog`} />
        <meta property="og:description" content={post.excerpt || post.title || 'Read this blog post from Fuwali Investments'} />
        <meta property="og:url" content={`https://fuwalizambia.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta property="article:published_time" content={post.published_at} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="blog-post-breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <ChevronRightIcon size={14} />
          <Link to="/blog">Blog</Link>
          <ChevronRightIcon size={14} />
          <span>{post.title}</span>
        </div>
      </div>

      {/* Post Header */}
      <article className="blog-post">
        <header className="blog-post-header">
          <div className="container">
            <div className="blog-post-meta">
              <span className="blog-post-date">
                <CalendarIcon size={16} />
                {formatDate(post.published_at)}
              </span>
              {post.author && (
                <span className="blog-post-author">By {post.author}</span>
              )}
              <span className="blog-post-views">
                <EyeOpenIcon size={16} />
                {post.view_count || 0} views
              </span>
              <span className="blog-post-comments">
                <MessageCircleIcon size={16} />
                {post.comment_count || 0} comments
              </span>
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            {post.excerpt && (
              <p className="blog-post-excerpt">{post.excerpt}</p>
            )}
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="blog-post-image">
            <div className="container">
              <img src={post.featured_image} alt={post.title} />
            </div>
          </div>
        )}

        {/* Post Content */}
        <div className="blog-post-content">
          <div className="container">
            <div 
              className="blog-post-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </div>

        {/* Comment Section */}
        <div className="blog-post-comments-section">
          <div className="container">
            <CommentSection postId={post.id} />
          </div>
        </div>

        {/* Post Footer */}
        <footer className="blog-post-footer">
          <div className="container">
            <Link to="/blog" className="btn btn-outline">
              Back to Blog
            </Link>
          </div>
        </footer>
      </article>
    </>
  );
};

export default BlogPost;
